import * as React from 'react';
import { IServerUserInfo } from '../../ts/models/IServerUserInfo';
import { IResult } from '../../httpClient/serverApi/types/IResponse';
import { UserSettingsNotificationParams, UserSettingsNotificationType, UserSettingsNotification } from '../notifications/UserSettingsNotification';
import { incrementRouterSpaceReactKey } from '../../app/actions/spaces/IncrementRouterSpaceReactKeyAction';
import { FormikHelpers, FormikProps, Formik, useFormik } from 'formik';
import { UserSettings } from '../services/resources/UserSettings';
import { ListItem, List, DropdownButtonType, CheckBoxWithLabelWidget } from '@tableau/widgets';
import { UserSettingsStyle } from '../styles/UserSettingsStyle';
import { FormikSingleSelectDropdown } from '../../commonInfra/components/FormikSingleSelectDropdown';
import { InlineCancelAndSaveButtons } from './UserSettingEditButtons';
import { Messages } from '../../codegen/Localize';
import { TimeZones, IGetTimeZonesResult } from '../services/TimeZones';
import { ITimeZone } from '../../ts/ServerApi/types/ITimeZone';
import _ = require('underscore');
import { HelpService } from '../../commonInfra/services/HelpService';
import { getAppStore } from '../../app/AppStore';
import {
  getSessionManagementServiceInstance,
  SessionManagementService
} from '../../sessionManagement/SessionManagementService';

export interface TimeZoneFormFields {
  timeZoneId: string;
}

export type TimeZoneFormProps = {
  initialValues: TimeZoneFormFields;
  serverUser: IServerUserInfo;
}

const userIsViewingTheirOwnSettings = (serverService: SessionManagementService, serverUser: IServerUserInfo) => {
  return serverService.sessionUsername() === serverUser.username
    && serverService.sessionUserDomainName() === serverUser.domainName;
};

const notifyTimeZoneChange = (result: IResult, username: string) => {
  const userSettingsNotificationParams: UserSettingsNotificationParams = {
    result: result,
    username: username,
    userSettingsNotificationType: UserSettingsNotificationType.ChangeTimeZone
  };
  UserSettingsNotification.notify(userSettingsNotificationParams);
};

function showTimeZoneButton(formik: FormikProps<TimeZoneFormFields>, initialTimeZoneId: string): boolean {
  if (!formik.dirty || !formik.isValid || formik.isSubmitting || (formik.values.timeZoneId.trim() === initialTimeZoneId)) {
    return false;
  }
  return true;
}

export function TimeZoneForm(props: TimeZoneFormProps): JSX.Element {
  const [curatedItems, setCuratedItems] = React.useState([]);
  const [usersCurrentTimeZone, setUsersCurrentTimeZone] = React.useState('');
  const [allItems, setAllItems] = React.useState([]);
  const [showFullList, setShowFullList] = React.useState(false);

  React.useEffect(() => {
    async function fetchTimeZones() {
      let timeZonesResult: IGetTimeZonesResult = await TimeZones.getAll();
      let curatedItems: ListItem[] = timeZonesResult.curatedTimeZonesList.map((timeZone: ITimeZone) => ({
        content: timeZone.displayName,
        value: timeZone.timeZoneId,
        testId: timeZone.timeZoneId,
      }));
      let allItems: ListItem[] = timeZonesResult.fullTimeZonesList.map((timeZone: ITimeZone) => ({
        content: timeZone.displayName,
        value: timeZone.timeZoneId,
        testId: timeZone.timeZoneId,
      }));

      const initialTimeZoneId = props.initialValues.timeZoneId;
      const serverTimeZone: ITimeZone = timeZonesResult.serverTimeZone;
      let currentTimeZoneId = initialTimeZoneId;
      let showFullList = false;
      let nullOrInvalidTimeZone = false;
      if (initialTimeZoneId) {
        const tzInCuratedList = _(timeZonesResult.curatedTimeZonesList).any(tz => tz.timeZoneId === initialTimeZoneId);
        const tzInExtraList = _(timeZonesResult.extraTimeZonesList).any(tz => tz.timeZoneId === initialTimeZoneId);
        if (!tzInCuratedList && !tzInExtraList) {
          currentTimeZoneId = serverTimeZone.timeZoneId;
          nullOrInvalidTimeZone = true;
        } else if (tzInExtraList) {
          showFullList = true;
        }
      } else {
        currentTimeZoneId = serverTimeZone.timeZoneId;
        nullOrInvalidTimeZone = true;
      }

      const serverTimeZoneInCuratedList = _(timeZonesResult.curatedTimeZonesList)
        .any(tz => tz.timeZoneId === serverTimeZone.timeZoneId);
      if (nullOrInvalidTimeZone && !serverTimeZoneInCuratedList) {
        showFullList = true;
      }
      setCuratedItems(curatedItems);
      setAllItems(allItems);
      setShowFullList(showFullList);
      setUsersCurrentTimeZone(currentTimeZoneId);
    }

    if (curatedItems.length === 0 && allItems.length === 0) {
      fetchTimeZones();
    }
  });

  const handleSubmit = React.useCallback(async (timeZoneId: string, formik: FormikHelpers<TimeZoneFormFields>) => {
    if (timeZoneId !== usersCurrentTimeZone) {
      const result: IResult = await UserSettings.updateUserLocalTimeZoneData(0, timeZoneId, true);
      const serverService: SessionManagementService = getSessionManagementServiceInstance();
      if (userIsViewingTheirOwnSettings(serverService, props.serverUser)) {
        await serverService.refreshSessionInfo();
        getAppStore().dispatch(incrementRouterSpaceReactKey());
      }
      if (result) {
        notifyTimeZoneChange(result, props.serverUser.username);
        setUsersCurrentTimeZone(timeZoneId);
      } else {
        formik.resetForm();
      }
    }
  }, [props, setUsersCurrentTimeZone]);

  const handleFullListCheckboxChange = React.useCallback((checked: boolean) => {
    setShowFullList(checked);
  }, [setShowFullList]);

  return (
    <Formik
      initialValues={{ timeZoneId: usersCurrentTimeZone }}
      onSubmit={function(values: TimeZoneFormFields, formik: FormikHelpers<TimeZoneFormFields>) {
        handleSubmit(values.timeZoneId, formik);
      }}
      enableReinitialize={true}
    >
      {(formik: FormikProps<TimeZoneFormFields>) => (
        <form onSubmit={formik.handleSubmit}>
          <table>
            <tbody className={UserSettingsStyle.tbodyNew}>
              {/* Time Zone Section */}
              <tr>
                <td className={UserSettingsStyle.labelTextNew}>
                    {Messages.userSettings_TimeZoneSetting_Header()}
                </td>
                <td className={UserSettingsStyle.detailsValue}>
                  {Messages.timeZoneHint()}
                  <a className="tb-lr-padded" rel="noopener noreferrer" target="_blank" href={HelpService.getServerHelpUrlByCategory('subscription-timezone')}>
                    <span>{Messages.timeZoneSettingLearnMore()}</span>
                  </a>
                </td>
              </tr>
              <tr data-test-id="time-zone-section">
                <td></td>
                <td className={UserSettingsStyle.detailsValueSmallPadding}>
                    <FormikSingleSelectDropdown
                        name="timeZoneId"
                        list={<List items={showFullList ? allItems : curatedItems} className={UserSettingsStyle.dropdownList}/>}
                        type={DropdownButtonType.Outline}
                        label={''}
                        className={UserSettingsStyle.dropdown}/>
                </td>
              </tr>
              <tr>
                <td></td>
                <td className={UserSettingsStyle.detailsValue}>
                  <CheckBoxWithLabelWidget
                    label={Messages.editTimeZoneShowFullTimeZoneListEnable()}
                    checked={showFullList}
                    testId={'show-full-time-zone-list'}
                    handleChange={handleFullListCheckboxChange}
                    containerStyle={{marginLeft: '0px'}}/>
                </td>
              </tr>
              {(showTimeZoneButton(formik, usersCurrentTimeZone) &&
                <InlineCancelAndSaveButtons
                testId={'edit-time-zone'}
                formik={formik}
                />
              )}
            </tbody>
          </table>
          <div>
          </div>
        </form>
      )}
    </Formik>
  );
}
